import { useState, type ComponentProps } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Input from "@/v2-ui/input"
import Badge from "@/v2-backoffice-client/components/badge"
import Link from "@/v2-backoffice-client/components/link"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import LicenseGoverningBadge from "@/v2-backoffice-client/license/LicenseGoverningBadge"
import UsageSparkline from "@/v2-backoffice-client/usage/UsageSparkline"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import { formatTrendDetail } from "@/v2-backoffice-client/usage/usage.constants"
import { formatLicenseDeniedReason } from "@/v2-backoffice-client/usage/denied/usage.denied.constants"
import UsageMcpClientChips from "@/v2-backoffice-client/usage/mcp/UsageMcpClientChips"
import { formatMcpTool } from "@/v2-backoffice-client/usage/mcp/usage.mcp.constants"
import { mcpTrendPct } from "@/v2-backoffice-client/usage/mcp/usage.mcp.utils"
import { buildCustomerMcpLink } from "@/v2-backoffice-client/portfolio/portfolio.utils"
import type { PortfolioMcpCustomer } from "@/v2-backoffice-client/portfolio/portfolio.mcp.utils"

export type PortfolioMcpCustomersTableProps = ComponentProps<"div"> & {
  /** Every customer in scope with MCP activity, most 28-day calls first. */
  customers: PortfolioMcpCustomer[]
  /** License status names by code (getLicenseStatuses), for the badge. */
  licenseStatusNamesByCode: Map<number, string>
}

const PREVIEW_LIMIT = 15

// Every customer on the MCP, one row each: the license they hold for it,
// the people, calls since the first and in the last four weeks and seven
// days, the daily shape, when they last called, the AI clients and the top
// tool, and the refused sign-ins. The name opens the customer's MCP tab.
// Fifteen rows by default (the report's chart cut), the rest behind "Show
// all" or the search.
function PortfolioMcpCustomersTable(props: PortfolioMcpCustomersTableProps) {
  const { customers, licenseStatusNamesByCode, className, ...divProps } = props

  const [showAll, setShowAll] = useState(false)
  const [search, setSearch] = useState("")

  const searchTerm = search.trim().toLowerCase()
  const matching =
    searchTerm === ""
      ? customers
      : customers.filter((customer) =>
          customer.Name.toLowerCase().includes(searchTerm),
        )
  const visible =
    showAll || searchTerm !== "" ? matching : matching.slice(0, PREVIEW_LIMIT)
  const hiddenCount = matching.length - visible.length

  return (
    <div className={cn("flex flex-col gap-3", className)} {...divProps}>
      {customers.length > PREVIEW_LIMIT && (
        <div className="w-56 self-end">
          <Input
            type="search"
            size="xs"
            placeholder="Search customers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      {matching.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No customers match the search.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="usage-report-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>MCP license</th>
                <th
                  className="num"
                  title="People with a call in the last 28 days, of everyone who has ever called"
                >
                  People
                </th>
                <th className="num" title="Tool calls since the first one">
                  All time
                </th>
                <th className="num">Last 28 days</th>
                <th className="num">Last 7 days</th>
                <th>Daily shape</th>
                <th>Last call</th>
                <th>AI clients</th>
                <th>Top tool</th>
                <th>Refused</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((customer) => {
                const topTool = customer.TopTools[0]
                return (
                  <tr key={customer.ExternalCustomerId}>
                    <td>
                      <Link
                        to={buildCustomerMcpLink(customer.ExternalCustomerId)}
                        iconSrc={null}
                        className="block max-w-56 truncate font-medium"
                        title={customer.LicensePlanNames ?? undefined}
                      >
                        {customer.Name}
                      </Link>
                      {customer.FirstActiveAt && (
                        <span className="block text-[11px] text-muted-foreground">
                          since {formatDateString(customer.FirstActiveAt)}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap">
                      <LicenseGoverningBadge
                        size="xs"
                        license={{
                          LicenseStatusCode: customer.LicenseStatusCode,
                          LicenseTrialEndsAt: customer.LicenseTrialEndsAt,
                          LicensePlanNames: customer.LicensePlanNames,
                          LicensedSeats: customer.LicensedSeats,
                          LicenseRenewsAt: customer.LicenseRenewsAt,
                          LicenseEndedAt: customer.LicenseEndedAt,
                        }}
                        statusName={licenseStatusNamesByCode.get(
                          customer.LicenseStatusCode,
                        )}
                      />
                    </td>
                    <td className="num">
                      {customer.Users28d}
                      <span className="text-muted-foreground">
                        {" "}
                        / {customer.UsersSeen}
                      </span>
                    </td>
                    <td className="num">{formatNumber(customer.CallsTotal)}</td>
                    <td className="num">
                      <span className="inline-flex items-center justify-end gap-1.5 whitespace-nowrap">
                        {customer.Calls28d > 0
                          ? formatNumber(customer.Calls28d)
                          : "—"}
                        {(customer.Calls28d > 0 ||
                          customer.CallsPrev28d > 0) && (
                          <UsageTrendBadge
                            trendPct={mcpTrendPct(
                              customer.Calls28d,
                              customer.CallsPrev28d,
                            )}
                            detail={formatTrendDetail(
                              customer.Calls28d,
                              customer.CallsPrev28d,
                            )}
                          />
                        )}
                      </span>
                    </td>
                    <td className="num">
                      {customer.Calls7d > 0 ? (
                        formatNumber(customer.Calls7d)
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td>
                      <UsageSparkline
                        series={customer.Daily28d}
                        className="h-5 w-24"
                      />
                    </td>
                    <td className="whitespace-nowrap text-muted-foreground">
                      {customer.LastActiveAt ? (
                        <RelativeDate date={customer.LastActiveAt} />
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <UsageMcpClientChips clients={customer.Clients} />
                    </td>
                    <td className="max-w-44">
                      {topTool ? (
                        <span className="block truncate" title={topTool.Tool}>
                          {formatMcpTool(topTool.Tool)}
                          <span className="text-muted-foreground">
                            {" "}
                            · {formatNumber(topTool.Calls)}
                          </span>
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {customer.Denied28d > 0 ? (
                        <Badge
                          variant="warning"
                          size="xs"
                          tooltip={`${formatNumber(customer.Denied28d)} refused sign-in${customer.Denied28d === 1 ? "" : "s"} from ${customer.DeniedUsers28d} ${customer.DeniedUsers28d === 1 ? "person" : "people"} in 28 days · ${formatLicenseDeniedReason(customer.DeniedReason)}`}
                        >
                          {formatNumber(customer.Denied28d)} ·{" "}
                          {customer.DeniedUsers28d}{" "}
                          {customer.DeniedUsers28d === 1 ? "person" : "people"}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {hiddenCount > 0 && (
        <button
          type="button"
          className="self-start text-xs text-muted-foreground underline-offset-2 hover:underline"
          onClick={() => setShowAll(true)}
        >
          Show all {matching.length} customers
        </button>
      )}
    </div>
  )
}

export default PortfolioMcpCustomersTable
